import { useEffect, useMemo, useState } from 'react';
import {
    CommodityMonthlyExportModel,
    SeasonalYearAverageModel,
    SeasonalYearAverageMonthlyValueMonthly,
    SeasonalYearByWeekAverageModel,
} from '../../../../Generated/Raven-Demeter';
import { GetCommodityMonthlySeasonalResponse, SeasonalMonthlyRowModel } from '../../../Apis/Hooks/useSeasonalApiHook';
import useLanguage from '../../../Services/Language/useLanguageHook';
import TableSkeleton from '../../Layout/TableSkeleton';
import Table from '../Table/Table';
import { TableDataFormat } from '../Table/TableDefinitions';
import {
    AverageOrTotalsType,
    MonthIndex,
    SeasonalColumnDefinition,
    SeasonalModel,
    seasonalTableBase,
    seasonalTableDefaultColumnOptions,
    yearlyAverageOrTotalColumnDefinitions,
} from './SeasonalTableDefinitions';
import styles from './SeasonalTables.module.scss';

export interface ISeasonalTableRawProps {
    seasonalData: GetCommodityMonthlySeasonalResponse;
    isPercent: boolean;
    displayThreeYearValues?: boolean;
    displayFiveYearValues?: boolean;
    displayQuarterlyAndYearlyValues?: boolean;
    displayDecimalPlacesMinimum?: number | undefined;
    displayDecimalPlacesMaximum?: number | undefined;
}

const defaultDecimalPlacesForPercent = 2;

const SeasonalTableRaw: React.FC<ISeasonalTableRawProps> = (props: ISeasonalTableRawProps) => {
    const [rowData, setRowData] = useState<SeasonalModel[]>([]);
    const [translations] = useLanguage();

    const seasonalMonthlyData = useMemo(
        () => props.seasonalData?.rows?.map((row) => row as SeasonalMonthlyRowModel).filter((row) => row) ?? [],
        [props.seasonalData],
    );

    const getBaseSeasonalTable = () =>
        seasonalTableBase.map((element) => {
            const newChildren = element.children?.map((child, index) => {
                if (index === 0) {
                    return { ...child, headerName: props.isPercent ? translations.words.shortYearOverYear : translations.words.year };
                }

                return child;
            });

            return { ...element, children: newChildren };
        });

    const headerValueGetter = (isAverageOrTotal: AverageOrTotalsType, prefix?: string) => {
        let header = translations.words.change;
        if (!props.isPercent) {
            if (isAverageOrTotal === 'total') {
                header = translations.words.total;
            } else {
                header = translations.text.averageShort;
            }
        }
        return `${prefix ?? ''} ${header}`;
    };

    const getYearlyAverageOrTotalColumnDefinitions = (isAverageOrTotal: AverageOrTotalsType) =>
        yearlyAverageOrTotalColumnDefinitions.map((element) => {
            const newChildren = element.children.map((child, index) => {
                const quarterlyPrefix = `${child.field![0]}${index + 1}`.toUpperCase();
                return {
                    ...child,
                    headerValueGetter: () => headerValueGetter(isAverageOrTotal, element.isYearly ? undefined : quarterlyPrefix),
                };
            });

            return { ...element, hide: !props.displayQuarterlyAndYearlyValues, children: newChildren };
        });

    const [columnDefinitions, setColumnDefinitions] = useState<SeasonalColumnDefinition[]>(getBaseSeasonalTable());

    useEffect(() => {
        if (seasonalMonthlyData.length === 0) {
            return;
        }

        const isAverage = seasonalMonthlyData[0].monthlyValues![0].alwaysShowActualValue;
        const additionalColumnDefinitions = getYearlyAverageOrTotalColumnDefinitions(isAverage ? 'average' : 'total');
        const dataFormat: TableDataFormat = props.isPercent ? 'percent' : 'currency';

        additionalColumnDefinitions.map((x) =>
            (x.children as SeasonalColumnDefinition[]).forEach((child) => {
                if (props.displayQuarterlyAndYearlyValues) {
                    child.hide = false;
                    child.displayDecimalPlaces = props.isPercent ? defaultDecimalPlacesForPercent : undefined;
                    child.dataFormat = dataFormat;
                } else {
                    child.hide = true;
                    child.dataFormat = dataFormat;
                    child.displayDecimalPlaces = props.isPercent ? defaultDecimalPlacesForPercent : undefined;
                }
            }),
        );

        setColumnDefinitions([...getBaseSeasonalTable(), ...additionalColumnDefinitions] as SeasonalColumnDefinition[]);

        const getBaseSeasonalModel = (
            x: SeasonalMonthlyRowModel | Partial<SeasonalYearAverageModel & SeasonalYearByWeekAverageModel>,
            label: string | number,
            id: string,
        ) => {
            const seasonalModel: SeasonalModel = {
                year: label,
                id,
                isPercent: props.isPercent,
                displayDecimalPlacesMinimum: props.displayDecimalPlacesMinimum,
                displayDecimalPlacesMaximum: props.displayDecimalPlacesMaximum,
            };

            x.monthlyValues?.forEach((monthData) => {
                const monthKey = `month${monthData.month as MonthIndex}` as const;
                const flagKey = `month${monthData.month as MonthIndex}IsActualValue` as const;

                if ((x as SeasonalYearAverageModel).numberOfYears) {
                    const currentAverageMonthData = monthData as SeasonalYearAverageMonthlyValueMonthly;
                    const value = currentAverageMonthData.averageValue;
                    seasonalModel[monthKey] = value!;
                    seasonalModel[flagKey] = true;
                } else {
                    const currentMonthData = monthData as CommodityMonthlyExportModel;
                    const value = props.isPercent ? currentMonthData.monthOverMonthAbsoluteChangePercent : currentMonthData.value;
                    seasonalModel[monthKey] = value!;
                    seasonalModel[flagKey] = currentMonthData.isActualValue;
                }
            });

            return seasonalModel;
        };

        const newSeasonalRowData = seasonalMonthlyData.map((row) => {
            const seasonalModel = getBaseSeasonalModel(row, row.year!, `${row.year}`);

            row.quarterlyValues?.forEach((quarterData) => {
                if (quarterData?.monthCount !== 3) {
                    return;
                }

                const totalOrAverageValueQuarter = isAverage ? quarterData.valueAverage : quarterData.valueTotal;

                seasonalModel[`quarter${quarterData.quarter}` as keyof SeasonalModel] = (
                    props.isPercent ? quarterData.quarterOverQuarterChangePercent : totalOrAverageValueQuarter
                ) as never;
                seasonalModel[`quarter${quarterData.quarter}IsActualValue` as keyof SeasonalModel] = quarterData.isActualValue as never;
            });

            if (row.yearlyValues?.monthCount === 12) {
                const totalOrAverageValueYearly = isAverage ? row.yearlyValues.valueAverage : row.yearlyValues.valueTotal;

                seasonalModel.yearlyAverageOrTotal = props.isPercent
                    ? row.monthlyValues![11].rollingYearChangePercent ?? undefined
                    : totalOrAverageValueYearly!;
                seasonalModel.yearlyAverageOrTotalIsActualValue = row.yearlyValues?.isActualValue;
            }

            return seasonalModel;
        });

        const threeYearAverageRow =
            props.displayThreeYearValues && props.seasonalData.averages
                ? props.seasonalData.averages
                      .filter((x) => x.numberOfYears === 3)
                      .map((x) =>
                          getBaseSeasonalModel(x, `${x.numberOfYears!} ${translations.words.year} ${translations.words.average}`, `${x.numberOfYears!}`),
                      )
                : [];

        const fiveYearAverageRow =
            props.displayFiveYearValues && props.seasonalData.averages
                ? props.seasonalData.averages
                      .filter((x) => x.numberOfYears === 5)
                      .map((x) =>
                          getBaseSeasonalModel(x, `${x.numberOfYears!} ${translations.words.year} ${translations.words.average}`, `${x.numberOfYears!}`),
                      )
                : [];

        setRowData([...threeYearAverageRow, ...fiveYearAverageRow, ...newSeasonalRowData]);
    }, [
        props.seasonalData,
        props.displayQuarterlyAndYearlyValues,
        props.displayThreeYearValues,
        props.displayFiveYearValues,
        props.isPercent,
        props.displayDecimalPlacesMinimum,
        props.displayDecimalPlacesMaximum,
    ]);

    const tableHeaders = useMemo(() => {
        const defaultDecimalPlaces = props.isPercent ? defaultDecimalPlacesForPercent : undefined;
        const dataFormat: TableDataFormat = props.isPercent ? 'percent' : 'currency';

        return columnDefinitions.map((x, index) => {
            if (index === 0) {
                return {
                    ...columnDefinitions[0],
                    children: columnDefinitions[0].children!.map((y, childIndex) => {
                        if (childIndex === 0) {
                            return y;
                        }

                        return { ...y, dataFormat, displayDecimalPlaces: defaultDecimalPlaces };
                    }),
                };
            }

            return x;
        });
    }, [columnDefinitions, props.isPercent]);

    return !rowData || rowData.length === 0 ? (
        <TableSkeleton />
    ) : (
        <div className={props.isPercent ? styles.seasonal_table_percent_container : styles.seasonal_table_values_container}>
            <Table
                tableHeaders={tableHeaders}
                defaultHeaderOptions={seasonalTableDefaultColumnOptions as SeasonalColumnDefinition}
                tableData={rowData}
                maximumHeight={300}
                tableStyle="alternatingRows"
                isScrollable
                hasGroupHeaders
            />
            <div className={styles.seasonal_table_legend_container}>
                {seasonalMonthlyData.some((x) => x.monthlyValues?.some((y) => !y.isActualValue)) && (
                    <div className={styles.seasonal_table_forecast_container}>
                        {props.isPercent && <p className={styles.seasonal_table_year_over_year_label}>{translations.text.shortYearOverYearChange}</p>}
                        <div className={styles.seasonal_table_forecast_square} />
                        <p>{translations.words.forecast}</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SeasonalTableRaw;
